/**
 * The users referential, filtered by the stores selected in the top menu and by
 * the platform. Admin and DHL only; DHL users cannot edit admins.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import { PLATFORM, ROLES, type Platform } from '@/shared/constants';
import {
  appConfigQueryOptions,
  getSelectedStoreIds,
  type StoreId,
} from '@/shared/config/app-config';
import { getUserProfile } from '@/features/auth/session';
import { hardNavigate } from '@/shared/lib/navigation';
import { TopMenu } from '@/shared/layout/TopMenu';
import { SortableHeader } from '@/shared/ui/SortableHeader';
import { workshopsQueryOptions } from '@/features/stores/api';
import plusIcon from '@/assets/images/plus.svg';
import { setVillaDior, usersQueryOptions, type UserRecord } from './api';
import { UserDialog } from './UserDialog';
import {
  DEFAULT_SORT,
  nextSort,
  roleName,
  sortUsers,
  storeSummary,
  visibleUsers,
  workshopNames,
  type SortKey,
} from './logic';

const MANAGING_ROLES: string[] = [ROLES.ADMIN, ROLES.DHL];

export function UsersPage({
  platform = PLATFORM.ABCDIOR,
}: Readonly<{
  platform?: Platform;
}>) {
  const user = getUserProfile();
  const canManage = !!user && MANAGING_ROLES.includes(user.role);
  const isDms = platform === PLATFORM.DMS;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user && !canManage) hardNavigate('/main');
  }, [user, canManage]);

  const users = useQuery({ ...usersQueryOptions(), enabled: canManage });
  const { data: workshops = [] } = useQuery({
    ...workshopsQueryOptions(),
    enabled: canManage,
  });
  const { data: config } = useQuery(appConfigQueryOptions());
  const stores = useMemo(() => config?.stores ?? [], [config?.stores]);
  const languages = useMemo(() => config?.languages ?? [], [config?.languages]);
  const [selectedIds, setSelectedIds] = useState<StoreId[]>(() =>
    getSelectedStoreIds(),
  );
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [dialog, setDialog] = useState<{ item: UserRecord | null } | null>(
    null,
  );

  const items = useMemo(
    () =>
      sortUsers(visibleUsers(users.data ?? [], platform, selectedIds), sort),
    [users.data, platform, selectedIds, sort],
  );

  // The box only moves once the API has answered.
  const [villaDiorPending, setVillaDiorPending] = useState<Set<string>>(
    () => new Set(),
  );
  const villaDior = useMutation({
    mutationFn: ({ item, active }: { item: UserRecord; active: boolean }) =>
      setVillaDior(item.id, active),
    onMutate: ({ item }) =>
      setVillaDiorPending((pending) => new Set(pending).add(item.id)),
    onSuccess: (_, { item, active }) =>
      queryClient.setQueryData(
        usersQueryOptions().queryKey,
        (current: UserRecord[] | undefined) =>
          current?.map((user) =>
            user.id === item.id
              ? {
                  ...user,
                  dmsSettings: { ...user.dmsSettings, villaDior: active },
                }
              : user,
          ),
      ),
    onError: (error) => {
      console.error(error);
      toast.error('Unexpected error, please try again later');
    },
    onSettled: (_, __, { item }) =>
      setVillaDiorPending((pending) => {
        const next = new Set(pending);
        next.delete(item.id);
        return next;
      }),
  });

  const closeDialog = (result: 'Confirm' | 'Cancel') => {
    setDialog(null);
    if (result === 'Confirm') {
      void queryClient.invalidateQueries({
        queryKey: usersQueryOptions().queryKey,
      });
    }
  };

  if (!canManage) return null;

  const canEdit = (item: UserRecord) =>
    user!.role !== ROLES.DHL || item.role !== ROLES.ADMIN;

  const sortableHeader = (key: SortKey, label: string) => (
    <SortableHeader
      label={label}
      width="20%"
      active={sort.by === key}
      descending={sort.reverse}
      onClick={() => setSort((current) => nextSort(current, key))}
      testId={`user-header-${key}`}
    />
  );

  return (
    <div className="container summary">
      <TopMenu platform={platform} onStoreSelected={setSelectedIds} />
      <div className="summary-table my-12">
        <button
          type="button"
          className="add-btn mb-4 flex items-center justify-center self-center"
          data-testid="header-add-user"
          onClick={() => setDialog({ item: null })}
        >
          <img src={plusIcon} alt="" />
          <span className="pl-2">Add a user</span>
        </button>
        <div className="table-container mb-4">
          <div className="items-container" role="region" aria-label="Users">
            <table className="table items">
              <thead>
                <tr>
                  {sortableHeader('username', 'User Name')}
                  {sortableHeader('email', 'Email')}
                  {sortableHeader('role', 'Role')}
                  <th style={{ width: '20%' }}>Assigned Stores</th>
                  {isDms ? (
                    <SortableHeader
                      label="Villa Dior"
                      width="10%"
                      active={sort.by === 'villaDior'}
                      descending={sort.reverse}
                      onClick={() =>
                        setSort((current) => nextSort(current, 'villaDior'))
                      }
                      testId="user-header-villa-dior"
                    />
                  ) : (
                    <th style={{ width: '10%' }}>Workshop</th>
                  )}
                  <th style={{ width: '10%' }} className="edit-header">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const storesLabel = storeSummary(stores, item.assignedStores);
                  const workshopsLabel = workshopNames(item, workshops);
                  return (
                    <tr key={item.id} className="user-row">
                      <td
                        style={{ width: '20%' }}
                        title={item.username ?? ''}
                        data-testid="user-row-user-name"
                      >
                        {item.username}
                      </td>
                      <td
                        style={{ width: '20%' }}
                        title={item.email ?? ''}
                        data-testid="user-row-email"
                      >
                        {item.email}
                      </td>
                      <td
                        style={{ width: '20%' }}
                        title={roleName(item.role)}
                        data-testid="user-row-role"
                      >
                        {roleName(item.role)}
                      </td>
                      <td
                        style={{ width: '20%' }}
                        title={storesLabel}
                        data-testid="user-row-user-stores"
                      >
                        {storesLabel}
                      </td>
                      {isDms ? (
                        <td style={{ width: '10%' }}>
                          <input
                            type="checkbox"
                            aria-label={`Villa Dior ${item.username ?? ''}`}
                            data-testid="user-row-villa-dior-checkbox"
                            checked={!!item.dmsSettings?.villaDior}
                            disabled={villaDiorPending.has(item.id)}
                            onChange={(event) =>
                              villaDior.mutate({
                                item,
                                active: event.target.checked,
                              })
                            }
                          />
                        </td>
                      ) : (
                        <td
                          style={{ width: '10%' }}
                          title={workshopsLabel}
                          data-testid="user-row-workshop"
                        >
                          {workshopsLabel}
                        </td>
                      )}
                      <td style={{ width: '10%' }} className="edit-action">
                        {canEdit(item) && (
                          <button
                            type="button"
                            className="button"
                            onClick={() => setDialog({ item })}
                            data-testid="user-row-edit-button"
                          >
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {users.isFetching && (
        <FontAwesomeIcon
          icon={faCircleNotch}
          spin
          className="pointer-cursor loading-icon"
          aria-label="Loading"
        />
      )}
      {dialog && (
        <UserDialog
          key={dialog.item?.id ?? 'new'}
          item={dialog.item}
          platform={platform}
          stores={stores}
          languages={languages}
          onClose={closeDialog}
        />
      )}
    </div>
  );
}
